/**
 * Gate 9 — Effort Proportionality
 *
 * Detects effort-output mismatch. EXISTENTIAL questions must
 * consume a substantial fraction of their token budget; ROUTINE
 * questions must not massively over-invest (trivializing the
 * signal budget).
 *
 * EXISTENTIAL: tokensSpent / targetTokens must be ≥ 0.80
 * MATERIAL:    tokensSpent / targetTokens must be ≥ 0.60
 * ELEVATED:    no minimum enforcement; no maximum
 * ROUTINE:     tokensSpent / targetTokens must be ≤ 4.0
 *
 * When no effortBudget is present, the gate passes (insufficient data).
 *
 * Trip routes to abstention with reason='effort_disproportionate'.
 */

export const EFFORT_RULES = {
  EXISTENTIAL: { minRatio: 0.80, maxRatio: null },
  MATERIAL: { minRatio: 0.60, maxRatio: null },
  ELEVATED: { minRatio: null, maxRatio: null },
  ROUTINE: { minRatio: null, maxRatio: 4.0 },
};

/**
 * @param {import('../disciplineEnforcer.js').EnforcerEnvelope} envelope
 * @param {object} [_config]
 * @returns {import('../disciplineEnforcer.js').GateResult}
 */
export function checkEffortProportionality(envelope, _config = {}) {
  const effortBudget = envelope.context?.effortBudget;

  if (!effortBudget || effortBudget.targetTokens == null || effortBudget.tokensSpent == null) {
    return { gateName: 'effort_proportionality', verdict: 'pass', reason: null, payload: { skipped: true } };
  }

  const { tokensSpent, targetTokens } = effortBudget;

  if (targetTokens === 0) {
    return { gateName: 'effort_proportionality', verdict: 'pass', reason: null, payload: { skipped: true, zeroBudget: true } };
  }

  const band = envelope.context?.materialityBand || 'ROUTINE';
  const rules = EFFORT_RULES[band] || EFFORT_RULES.ROUTINE;
  const ratio = tokensSpent / targetTokens;

  if (rules.minRatio != null && ratio < rules.minRatio) {
    return {
      gateName: 'effort_proportionality',
      verdict: 'trip',
      reason: 'effort_disproportionate',
      payload: { band, ratio, minRatio: rules.minRatio, maxRatio: rules.maxRatio, tokensSpent, targetTokens, violation: 'under_invested' },
    };
  }

  if (rules.maxRatio != null && ratio > rules.maxRatio) {
    return {
      gateName: 'effort_proportionality',
      verdict: 'trip',
      reason: 'effort_disproportionate',
      payload: { band, ratio, minRatio: rules.minRatio, maxRatio: rules.maxRatio, tokensSpent, targetTokens, violation: 'over_invested' },
    };
  }

  return {
    gateName: 'effort_proportionality',
    verdict: 'pass',
    reason: null,
    payload: { band, ratio, tokensSpent, targetTokens },
  };
}
